import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Trash2, Edit3, X, MapPin, Info } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
    pushim:    { label: "Pushim",    tone: "bg-terracotta/15 text-terracotta border-terracotta/40", dot: "bg-terracotta" },
    provim:    { label: "Provim",    tone: "bg-amber-100 text-amber-800 border-amber-300",          dot: "bg-amber-500" },
    aktivitet: { label: "Aktivitet", tone: "bg-sage/15 text-sage border-sage/40",                   dot: "bg-sage" },
    takim:     { label: "Takim",     tone: "bg-oxford/10 text-oxford border-oxford/30",             dot: "bg-oxford" },
};
const DAY_LABELS = ["H", "M", "M", "E", "P", "Sh", "D"];
const MONTH_LABELS = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];

function ymd(d) { return d.toISOString().slice(0, 10); }
function parseYMD(s) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

const emptyForm = (cls_id = "") => ({
    title: "",
    description: "",
    event_type: "pushim",
    date_from: new Date().toISOString().slice(0, 10),
    date_to: "",
    class_id: cls_id,
    notify: "none",
});

export default function Calendar() {
    const { user } = useAuth();
    const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [myClasses, setMyClasses] = useState([]);  // for kujdestari
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(() => emptyForm());
    const [submitting, setSubmitting] = useState(false);
    const [detail, setDetail] = useState(null);

    const canCreateSchoolWide = user?.role === "super_admin" || user?.role === "secretary";
    const canCreateClassScoped = user?.role === "teacher" && myClasses.length > 0;
    const canCreate = canCreateSchoolWide || canCreateClassScoped;

    const month = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

    useEffect(() => {
        if (user?.role === "teacher") {
            api.get("/kujdestari/my-classes").then((r) => setMyClasses(r.data)).catch(() => setMyClasses([]));
        }
    }, [user?.role]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/events", { params: { month } });
            setEvents(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [month]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm(canCreateSchoolWide ? "" : myClasses[0]?.id || ""));
        setDialogOpen(true);
    };
    const openEdit = (ev) => {
        setEditing(ev);
        setForm({
            title: ev.title,
            description: ev.description || "",
            event_type: ev.event_type,
            date_from: ev.date_from,
            date_to: ev.date_to && ev.date_to !== ev.date_from ? ev.date_to : "",
            class_id: ev.class_id || "",
            notify: "none",
        });
        setDetail(null);
        setDialogOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return toast.error("Titulli është i detyrueshëm");
        setSubmitting(true);
        try {
            const payload = {
                title: form.title,
                description: form.description,
                event_type: form.event_type,
                date_from: form.date_from,
                date_to: form.date_to || null,
                class_id: form.class_id || null,
                notify: form.notify,
            };
            if (editing) {
                await api.patch(`/events/${editing.id}`, payload);
                toast.success("Ngjarja u përditësua");
            } else {
                await api.post("/events", payload);
                toast.success("Ngjarja u shtua");
            }
            setDialogOpen(false);
            load();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    const removeEvent = async (id) => {
        if (!window.confirm("Të fshihet ngjarja?")) return;
        try {
            await api.delete(`/events/${id}`);
            toast.success("U fshi");
            setDetail(null);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const grid = useMemo(() => {
        const first = startOfMonth(cursor);
        const last = endOfMonth(cursor);
        const dow = (first.getDay() + 6) % 7;  // Monday=0
        const cells = [];
        for (let i = 0; i < dow; i++) cells.push(null);
        for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [cursor]);

    const eventsByDay = useMemo(() => {
        const map = {};
        for (const e of events) {
            const from = parseYMD(e.date_from);
            const to = parseYMD(e.date_to || e.date_from);
            const d = new Date(from);
            while (d <= to) {
                const key = ymd(d);
                (map[key] = map[key] || []).push(e);
                d.setDate(d.getDate() + 1);
            }
        }
        return map;
    }, [events]);

    const todayStr = ymd(new Date());
    const upcoming = useMemo(() => {
        return events
            .filter((e) => (e.date_to || e.date_from) >= todayStr)
            .sort((a, b) => a.date_from.localeCompare(b.date_from))
            .slice(0, 6);
    }, [events, todayStr]);

    return (
        <Layout>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-2">
                        <CalendarIcon className="w-3 h-3" /> Kalendari Shkollor
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
                        {MONTH_LABELS[cursor.getMonth()]} {cursor.getFullYear()}
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">Pushime, provime, aktivitete dhe takime</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setCursor(startOfMonth(new Date()))} data-testid="cal-today">Sot</Button>
                    <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
                    {canCreate && (
                        <Button onClick={openCreate} className="gap-2 bg-oxford hover:bg-oxfordhover ml-2" data-testid="cal-add-event">
                            <Plus className="w-4 h-4" /> Shto ngjarje
                        </Button>
                    )}
                </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-4">
                {Object.entries(TYPE_META).map(([k, m]) => (
                    <span key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} /> {m.label}
                    </span>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
                {/* Calendar grid */}
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="grid grid-cols-7 bg-bone/40 border-b border-slate-200 text-center">
                        {DAY_LABELS.map((d, i) => (
                            <div key={i} className="text-[10px] uppercase tracking-widest font-bold text-slate-600 py-2">{d}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-7" data-testid="calendar-grid">
                        {grid.map((d, i) => {
                            if (!d) return <div key={i} className="aspect-square sm:min-h-[96px] bg-slate-50/50 border border-slate-100" />;
                            const key = ymd(d);
                            const dayEvents = eventsByDay[key] || [];
                            const isToday = sameDay(d, new Date());
                            return (
                                <div key={i} className={`min-h-[72px] sm:min-h-[110px] border border-slate-100 p-1.5 flex flex-col gap-0.5 ${isToday ? "bg-oxford/5" : ""}`} data-testid={`cal-day-${key}`}>
                                    <div className={`text-[11px] font-bold ${isToday ? "bg-oxford text-white rounded-full w-5 h-5 flex items-center justify-center" : "text-slate-600"}`}>
                                        {d.getDate()}
                                    </div>
                                    <div className="flex-1 space-y-0.5 overflow-hidden">
                                        {dayEvents.slice(0, 3).map((e) => (
                                            <button
                                                key={e.id + key}
                                                onClick={() => setDetail(e)}
                                                className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] font-semibold border truncate ${TYPE_META[e.event_type]?.tone || "bg-slate-100 border-slate-200"}`}
                                                title={e.title}
                                                data-testid={`event-${e.id}`}
                                            >
                                                {e.title}
                                            </button>
                                        ))}
                                        {dayEvents.length > 3 && (
                                            <button onClick={() => setDetail(dayEvents[3])} className="text-[10px] text-slate-500 hover:text-oxford font-semibold">+{dayEvents.length - 3}</button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Upcoming list */}
                <aside className="bg-white border border-slate-200 rounded-2xl overflow-hidden h-fit">
                    <div className="px-4 py-3 border-b border-slate-200 bg-bone/40">
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Të ardhshme</div>
                    </div>
                    {loading ? (
                        <div className="p-6 text-center text-sm text-slate-500">Duke ngarkuar…</div>
                    ) : upcoming.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500" data-testid="no-upcoming">Asnjë ngjarje aktive</div>
                    ) : (
                        <div className="divide-y divide-slate-200">
                            {upcoming.map((e) => (
                                <button key={e.id} onClick={() => setDetail(e)} className="w-full text-left px-4 py-3 hover:bg-bone/40 flex items-start gap-3" data-testid={`upcoming-${e.id}`}>
                                    <div className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${TYPE_META[e.event_type]?.dot || "bg-slate-400"}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-slate-900 text-sm truncate">{e.title}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            {e.date_from === e.date_to ? e.date_from : `${e.date_from} – ${e.date_to}`}
                                            {e.class_name && <> • {e.class_name}</>}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </aside>
            </div>

            {/* Create/Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={(o) => !submitting && setDialogOpen(o)}>
                <DialogContent className="max-w-lg" data-testid="event-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Përditëso ngjarjen" : "Shto ngjarje të re"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Titulli *</label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="p.sh., Dita e Flamurit" data-testid="event-title" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Lloji</label>
                            <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="event-type">
                                {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Nga data *</label>
                                <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="event-date-from" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Deri (opsionale)</label>
                                <Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="event-date-to" />
                            </div>
                        </div>
                        {canCreateClassScoped && !canCreateSchoolWide && (
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Klasa</label>
                                <select value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="event-class">
                                    {myClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Përshkrim (opsionale)</label>
                            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Detaje plotësuese…" data-testid="event-desc" />
                        </div>
                        {!editing && (
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Njoftim</label>
                                <select value={form.notify} onChange={(e) => setForm({ ...form, notify: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="event-notify">
                                    <option value="none">Vetëm në kalendar (pa njoftim)</option>
                                    <option value="inbox">Njoftim in-app (inbox)</option>
                                    <option value="inbox_email">In-app + email</option>
                                </select>
                            </div>
                        )}
                        <DialogFooter className="gap-2">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Anulo</Button>
                            <Button type="submit" disabled={submitting} className="bg-oxford hover:bg-oxfordhover" data-testid="event-submit">
                                {editing ? "Ruaj" : "Shto"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            {detail && (
                <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full overflow-hidden my-8" data-testid="event-detail">
                        <div className={`px-5 py-3 border-b border-slate-200 flex items-center gap-3 ${TYPE_META[detail.event_type]?.tone || ""}`}>
                            <span className={`w-2.5 h-2.5 rounded-full ${TYPE_META[detail.event_type]?.dot || "bg-slate-400"}`} />
                            <div className="text-xs uppercase tracking-widest font-bold">{TYPE_META[detail.event_type]?.label}</div>
                            <div className="ml-auto flex items-center gap-1">
                                {detail.can_edit && (
                                    <>
                                        <button onClick={() => openEdit(detail)} className="p-1.5 rounded-md hover:bg-white/40" title="Edito" data-testid="event-edit">
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => removeEvent(detail.id)} className="p-1.5 rounded-md hover:bg-white/40 text-red-600" title="Fshi" data-testid="event-delete">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </>
                                )}
                                <button onClick={() => setDetail(null)} className="p-1.5 rounded-md hover:bg-white/40">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="p-5">
                            <h3 className="font-heading text-xl font-bold text-slate-900">{detail.title}</h3>
                            <div className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                                <CalendarIcon className="w-3.5 h-3.5" />
                                {detail.date_from === detail.date_to ? detail.date_from : `${detail.date_from} – ${detail.date_to}`}
                            </div>
                            {detail.class_name && (
                                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {detail.class_name}</div>
                            )}
                            {detail.description && (
                                <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed">{detail.description}</p>
                            )}
                            <div className="text-[11px] text-slate-500 mt-4 flex items-center gap-1.5">
                                <Info className="w-3 h-3" /> Shtuar nga {detail.created_by_name}
                                {detail.created_by_role === "teacher" && " (Kujdestari)"}
                                {detail.created_by_role === "secretary" && " (Sekretar)"}
                                {detail.created_by_role === "super_admin" && " (Admin)"}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
